/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AnalyticsLogger } from "../../logging/AnalyticsLogger";
import { Log } from "../../logging/Log";
import { OperationalLogger } from "../../logging/OperationalLogger";
import { PlayerErrorSeverity } from "../../videoplayer/enums";
import type { PlayerState } from "../../videoplayer/enums";
import type { TPlayerError, TPlayerStats } from "../../videoplayer/types";
import { PlaybackSessionCoordinatorConstants, SessionEndReason } from "./enums";
import { PlaybackHealthLogger } from "./PlaybackHealthLogger";
import { PlaybackSessionAccumulatorImpl } from "./PlaybackSessionAccumulatorImpl";
import type { IPlaybackSessionAccumulator } from "./IPlaybackSessionAccumulator";
import { PlaybackSessionSnapshotSerializer } from "./PlaybackSessionSnapshotSerializer";
import type {
  TOperationalSnapshotInputs,
  TPlaybackSessionContext,
  TPlaybackSessionSnapshot
} from "./types";
import {
  buildAggregated,
  buildConfiguration,
  buildEntitlement,
  buildPgd,
  buildPgdDetails,
  buildSessionState,
  buildSnapshot,
  buildStartStatus,
  buildStatus,
  isPgdMetadataResolved,
  mapPlayerStateToBucket
} from "./utils";

const DEFAULT_OPERATIONAL_SNAPSHOT_INPUTS: TOperationalSnapshotInputs = {
  playerState: null,
  positionMs: 0,
  bufferedInfo: null,
  durationSec: 0,
  ccLanguage: undefined
};

/**
 * Lifecycle coordinator for the `playbackSession` analytics event, and for
 * the `playbackStart`/`playbackEnd` operational events which reuse the same
 * snapshot/serializer but are emitted via `OperationalLogger` instead.
 * Owns the time accumulator, tracks per-session error codes, builds the
 * grouped snapshot on session end, and emits it via `AnalyticsLogger`.
 *
 * Callers must invoke `start()` once per session (including at each program
 * boundary) and one of the terminal hooks (`onPlaybackStopped`/`destroy`)
 * exactly once per session — extra calls are safe no-ops (idempotency is
 * guarded by the internal `active` flag), which lets callers unconditionally
 * emit a specific end reason ahead of a generic teardown path.
 *
 * `playbackStart`/`playbackEnd`, `playbackHealth`, and `playbackSession`
 * share identical session-scoping rules — every session, including one
 * created at a same-channel program boundary, gets its own paired
 * `playbackStart`/`playbackEnd` — see `onProgramBoundary()`.
 */
export class PlaybackSessionCoordinator {
  private mAccumulator: IPlaybackSessionAccumulator;

  private mGetStats: () => TPlayerStats;

  private mGetIsClosedCaptionActive: () => boolean;

  private mGetOperationalSnapshotInputs: () => TOperationalSnapshotInputs;

  private mMonotonicNowMs: () => number;

  private mWallClockNowSeconds: () => number;

  private mActive: boolean = false;

  /** True while a `playbackStart` has been emitted for the current session. */
  private mOperationalActive: boolean = false;

  /** True while `playbackStart` is waiting on metadata to resolve real PGD titles. */
  private mOperationalStartPending: boolean = false;

  private mContext: TPlaybackSessionContext | null = null;

  private mSessionStartEpochSeconds: number = 0;

  private mTerminalErrorCodes: Set<string> = new Set();

  private mTransientErrorCodes: Set<string> = new Set();

  private mHealthLogger: PlaybackHealthLogger = new PlaybackHealthLogger();

  public constructor(
    getStats: () => TPlayerStats,
    getIsClosedCaptionActive: () => boolean = () => false,
    getOperationalSnapshotInputs: () => TOperationalSnapshotInputs = () =>
      DEFAULT_OPERATIONAL_SNAPSHOT_INPUTS,
    accumulator: IPlaybackSessionAccumulator = new PlaybackSessionAccumulatorImpl(),
    monotonicNowMs: () => number = () => performance.now(),
    wallClockNowSeconds: () => number = () =>
      Date.now() / PlaybackSessionCoordinatorConstants.MILLISECONDS_PER_SECOND
  ) {
    this.mGetStats = getStats;
    this.mGetIsClosedCaptionActive = getIsClosedCaptionActive;
    this.mGetOperationalSnapshotInputs = getOperationalSnapshotInputs;
    this.mAccumulator = accumulator;
    this.mMonotonicNowMs = monotonicNowMs;
    this.mWallClockNowSeconds = wallClockNowSeconds;
  }

  public isActive(): boolean {
    return this.mActive;
  }

  /** Begin a new session. Closes (as SUPERSEDED) any still-active session first. */
  public start(context: TPlaybackSessionContext): void {
    this.startInternal(context, true);
  }

  /**
   * Emit the finished program's `playbackEnd`/`playbackSession`, then start
   * a fresh session (with its own `playbackStart`) for the new program.
   * Per spec, a same-channel program boundary ends the current session and
   * begins a new one — startup timing fields will be zero since the player
   * is already active.
   */
  public onProgramBoundary(nextContext: TPlaybackSessionContext): void {
    if (!this.mActive) {
      return;
    }
    this.emitAndClose(SessionEndReason.END_OF_CONTENT);
    this.startInternal(nextContext, true);
  }

  private startInternal(
    context: TPlaybackSessionContext,
    emitOperationalStart: boolean
  ): void {
    if (this.mActive) {
      Log.w(
        PlaybackSessionCoordinatorConstants.TAG,
        "start() - previous session still active, superseding"
      );
      this.emitAndClose(SessionEndReason.SUPERSEDED);
    }
    this.mContext = context;
    this.mTerminalErrorCodes.clear();
    this.mTransientErrorCodes.clear();
    this.mSessionStartEpochSeconds = this.mWallClockNowSeconds();
    this.mAccumulator.start(this.mMonotonicNowMs());
    this.mActive = true;
    this.mHealthLogger.start(() => this.emitPlaybackHealth());
    if (emitOperationalStart) {
      if (isPgdMetadataResolved(context)) {
        this.emitOperationalStart(context);
      } else {
        this.mOperationalStartPending = true;
      }
    }
  }

  /**
   * Emits `playbackStart` — immediately, before any async DRM/session-
   * verification work, when PGD metadata is already resolved (spec §3.1's
   * sub-second requirement), or as soon as it resolves when `start()`
   * deferred it (see `startInternal()`/`updateMetadata()`).
   */
  private emitOperationalStart(context: TPlaybackSessionContext): void {
    const inputs = this.mGetOperationalSnapshotInputs();
    const pgd = buildPgd(context, inputs.providerId);
    const pgdDetails = buildPgdDetails(context);
    const configuration = buildConfiguration(context, inputs, this.mGetStats());
    const state = buildSessionState(
      context,
      this.mAccumulator.getPlaybackId(),
      this.mSessionStartEpochSeconds,
      this.mGetIsClosedCaptionActive()
    );

    const payload = PlaybackSessionSnapshotSerializer.toOperationalStartMap(
      pgd,
      pgdDetails,
      configuration,
      state,
      {},
      buildStartStatus()
    );
    OperationalLogger.logPlaybackStart(payload);
    Log.d(
      PlaybackSessionCoordinatorConstants.TAG,
      `emitOperationalStart() - ${PlaybackSessionCoordinatorConstants.EVENT_NAME_OPERATIONAL_START} event fired`
    );
    this.mOperationalActive = true;
  }

  /**
   * Mid-session heartbeat — fires on `mHealthLogger`'s cadence. Reads a
   * fresh live aggregate (`accumulator.snapshot()`, not `close()`) so the
   * counters keep growing across ticks without finalizing the session.
   * Guarded so a tick that lands after teardown (or before `playbackStart`
   * has fired) is a no-op.
   */
  private emitPlaybackHealth(): void {
    if (!this.mActive || !this.mOperationalActive || this.mContext === null) {
      return;
    }
    const context = this.mContext;
    const inputs = this.mGetOperationalSnapshotInputs();
    const pgd = buildPgd(context, inputs.providerId);
    const pgdDetails = buildPgdDetails(context);
    const stats = this.mGetStats();
    const configuration = buildConfiguration(context, inputs, stats);
    const state = buildSessionState(
      context,
      this.mAccumulator.getPlaybackId(),
      this.mSessionStartEpochSeconds,
      this.mGetIsClosedCaptionActive()
    );

    const accumulatorSnapshot = this.mAccumulator.snapshot(
      this.mMonotonicNowMs()
    );
    const transientCodesJoined =
      this.mTransientErrorCodes.size > 0
        ? Array.from(this.mTransientErrorCodes).join(",")
        : undefined;
    const userErrorCount =
      this.mTerminalErrorCodes.size + this.mTransientErrorCodes.size;
    const aggregated = buildAggregated(
      accumulatorSnapshot,
      stats,
      userErrorCount,
      transientCodesJoined
    );

    const playerSnapshot = buildSnapshot(
      inputs.playerState,
      inputs.positionMs,
      inputs.bufferedInfo,
      inputs.durationSec,
      inputs.ccLanguage
    );

    const payload = PlaybackSessionSnapshotSerializer.toOperationalHealthMap(
      pgd,
      pgdDetails,
      configuration,
      state,
      {},
      aggregated,
      playerSnapshot,
      this.mWallClockNowSeconds()
    );
    OperationalLogger.logPlaybackHealth(payload);
    Log.d(
      PlaybackSessionCoordinatorConstants.TAG,
      "emitPlaybackHealth() - playbackHealth event fired"
    );
  }

  public onPlayerStateChanged(
    previousState: PlayerState | null,
    newState: PlayerState,
    isTrickPlayActive: boolean
  ): void {
    if (!this.mActive) {
      return;
    }
    const bucket = mapPlayerStateToBucket(
      newState,
      previousState,
      this.mAccumulator.hasPlayed(),
      isTrickPlayActive
    );
    if (bucket !== null) {
      this.mAccumulator.onStateChanged(bucket, this.mMonotonicNowMs());
    }
  }

  /**
   * Updates session context values once their source is resolved. Metadata is
   * frequently unavailable at `start()` time, so updating it also flushes a
   * `playbackStart` that was deferred pending resolved PGD titles.
   */
  public updateSessionValues(
    values: Partial<
      Pick<TPlaybackSessionContext, "locality" | "metadata" | "streamingUrl">
    >
  ): void {
    if (!this.mActive || this.mContext === null) {
      return;
    }
    this.mContext = { ...this.mContext, ...values };
    if (values.metadata !== undefined && this.mOperationalStartPending) {
      this.mOperationalStartPending = false;
      this.emitOperationalStart(this.mContext);
    }
  }

  /** Accumulates an error code without ending the session (non-fatal/transient errors). */
  public recordPlaybackError(error: TPlayerError): void {
    if (!this.mActive) {
      return;
    }
    if (error.severity === PlayerErrorSeverity.CRITICAL) {
      this.mTerminalErrorCodes.add(error.code);
    } else {
      this.mTransientErrorCodes.add(error.code);
    }
  }

  public onPlaybackStopped(
    reason: SessionEndReason,
    error?: TPlayerError
  ): void {
    if (!this.mActive) {
      return;
    }
    if (error !== undefined) {
      this.recordPlaybackError(error);
    }
    this.emitAndClose(reason, error);
  }

  /** Controller teardown — emit only if a session is still active. */
  public destroy(): void {
    if (!this.mActive) {
      return;
    }
    this.emitAndClose(SessionEndReason.USER_ENDED);
  }

  private emitAndClose(
    reason: SessionEndReason,
    error?: TPlayerError,
    emitOperationalEnd: boolean = true
  ): void {
    this.mHealthLogger.stop();
    const context = this.mContext;
    if (context === null) {
      this.mActive = false;
      return;
    }

    const accumulatorSnapshot = this.mAccumulator.close(this.mMonotonicNowMs());
    const endTimeEpochSeconds = this.mWallClockNowSeconds();
    const stats = this.mGetStats();

    const terminalCodesJoined =
      this.mTerminalErrorCodes.size > 0
        ? Array.from(this.mTerminalErrorCodes).join(",")
        : undefined;
    const transientCodesJoined =
      this.mTransientErrorCodes.size > 0
        ? Array.from(this.mTransientErrorCodes).join(",")
        : undefined;
    const userErrorCount =
      this.mTerminalErrorCodes.size + this.mTransientErrorCodes.size;

    // Metadata never resolved before the run ended — flush the deferred
    // playbackStart now so every run still gets a paired start/end.
    if (this.mOperationalStartPending) {
      this.mOperationalStartPending = false;
      this.emitOperationalStart(context);
    }

    const inputs = this.mGetOperationalSnapshotInputs();
    const pgd = buildPgd(context, inputs.providerId);
    const pgdDetails = buildPgdDetails(context);
    const configuration = buildConfiguration(context, inputs, stats);
    const state = buildSessionState(
      context,
      accumulatorSnapshot.playbackId,
      this.mSessionStartEpochSeconds,
      this.mGetIsClosedCaptionActive()
    );

    const snapshot: TPlaybackSessionSnapshot = {
      pgd,
      pgdDetails,
      pcd: {},
      state,
      entitlement: buildEntitlement(context),
      aggregated: buildAggregated(
        accumulatorSnapshot,
        stats,
        userErrorCount,
        transientCodesJoined,
        pgdDetails.programStartTime,
        this.mSessionStartEpochSeconds
      ),
      status: buildStatus(
        accumulatorSnapshot,
        reason,
        endTimeEpochSeconds,
        terminalCodesJoined,
        transientCodesJoined,
        error
      )
    };

    const payload = PlaybackSessionSnapshotSerializer.toShortNameMap(snapshot);

    AnalyticsLogger.logPlaybackSession(payload);

    Log.d(
      PlaybackSessionCoordinatorConstants.TAG,
      `emitAndClose() - ${PlaybackSessionCoordinatorConstants.EVENT_NAME} event fired, reason=${reason}`
    );

    if (emitOperationalEnd && this.mOperationalActive) {
      const playerSnapshot = buildSnapshot(
        inputs.playerState,
        inputs.positionMs,
        inputs.bufferedInfo,
        inputs.durationSec,
        inputs.ccLanguage
      );
      const operationalPayload =
        PlaybackSessionSnapshotSerializer.toOperationalEndMap(
          snapshot,
          configuration,
          playerSnapshot
        );
      OperationalLogger.logPlaybackEnd(operationalPayload);
      Log.d(
        PlaybackSessionCoordinatorConstants.TAG,
        `emitAndClose() - ${PlaybackSessionCoordinatorConstants.EVENT_NAME_OPERATIONAL_END} event fired, reason=${reason}`
      );
      this.mOperationalActive = false;
    }

    this.mActive = false;
    this.mContext = null;
  }
}
